/**
 * Creator editor modal. Starts from a YouTube channel picker (Channel CRM +
 * YouTube search) that auto-fills identity: display name, subscribers, avatar
 * and banner — all still editable, with custom-URL overrides preserved on
 * refresh. Also: notification contacts, budget tiers, active toggle, and the
 * public request link (copy + regenerate; regeneration retires the old link).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal } from '../ui';
import {
  CreatorInput,
  createTakeoverCreator,
  lookupTakeoverChannel,
  regenerateTakeoverSlug,
  searchTakeoverChannels,
  updateTakeoverCreator,
} from '../../services/takeoversApi';
import {
  TakeoverBudgetTier,
  TakeoverChannelCandidate,
  TakeoverChannelDetails,
  TakeoverCreator,
  TakeoverCreatorContact,
  TakeoverCreatorYouTube,
} from './types';

interface CreatorEditorProps {
  creator: TakeoverCreator | null; // null = create
  onClose: () => void;
  onSaved: (creator: TakeoverCreator) => void;
  notify: (kind: 'success' | 'error', title: string, message?: string) => void;
}

const DEFAULT_TIERS: TakeoverBudgetTier[] = [
  { id: 'lt10k', label: '<$10K', maxUsd: 10000, sortOrder: 0 },
  { id: '10-25k', label: '$10–25K', minUsd: 10000, maxUsd: 25000, sortOrder: 1 },
  { id: '25-50k', label: '$25–50K', minUsd: 25000, maxUsd: 50000, sortOrder: 2 },
  { id: '50-100k', label: '$50–100K', minUsd: 50000, maxUsd: 100000, sortOrder: 3 },
  { id: '100k-plus', label: '$100K+', minUsd: 100000, sortOrder: 4 },
];

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  fontSize: 13.5,
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
  display: 'block',
  marginBottom: 4,
};

const hintStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--fg-4)', marginTop: 6 };

/** YouTube-hosted image URLs are safe to overwrite on refresh; custom URLs are kept. */
const isYouTubeHosted = (url: string): boolean =>
  /(ggpht\.com|googleusercontent\.com|ytimg\.com)\//i.test(url);

const fmtSynced = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const hideOnError = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.display = 'none';
};

const CreatorEditor: React.FC<CreatorEditorProps> = ({ creator, onClose, onSaved, notify }) => {
  const [displayName, setDisplayName] = useState(creator?.displayName ?? '');
  const [blurb, setBlurb] = useState(creator?.blurb ?? '');
  const [subsDisplay, setSubsDisplay] = useState(creator?.subsDisplay ?? '');
  const [avatarUrl, setAvatarUrl] = useState(creator?.avatarUrl ?? '');
  const [bannerUrl, setBannerUrl] = useState(creator?.bannerUrl ?? '');
  const [youtube, setYoutube] = useState<TakeoverCreatorYouTube | null>(creator?.youtube ?? null);
  const [active, setActive] = useState(creator?.active ?? true);
  const [contacts, setContacts] = useState<TakeoverCreatorContact[]>(creator?.contacts ?? []);
  const [tiers, setTiers] = useState<TakeoverBudgetTier[]>(
    creator?.budgetTiers?.length ? creator.budgetTiers : DEFAULT_TIERS,
  );
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Channel picker state
  const [ytQuery, setYtQuery] = useState('');
  const [ytResults, setYtResults] = useState<TakeoverChannelCandidate[]>([]);
  const [ytSearching, setYtSearching] = useState(false);
  const [ytNote, setYtNote] = useState<string | null>(null);
  const [ytBusy, setYtBusy] = useState(false); // linking or refreshing
  const [ytDetails, setYtDetails] = useState<TakeoverChannelDetails | null>(null);
  const searchSeq = useRef(0);

  useEffect(() => {
    const q = ytQuery.trim();
    if (q.length < 2) {
      setYtResults([]);
      setYtNote(null);
      setYtSearching(false);
      return;
    }
    setYtSearching(true);
    const seq = ++searchSeq.current;
    const timer = setTimeout(async () => {
      try {
        const { candidates, youtubeError } = await searchTakeoverChannels(q);
        if (seq !== searchSeq.current) return;
        setYtResults(candidates);
        setYtNote(youtubeError ?? null);
      } catch {
        if (seq !== searchSeq.current) return;
        setYtResults([]);
        setYtNote('Search failed — paste a channel URL, or fill the fields manually.');
      } finally {
        if (seq === searchSeq.current) setYtSearching(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [ytQuery]);

  const applyDetails = (d: TakeoverChannelDetails, mode: 'link' | 'refresh') => {
    setYoutube({
      channelId: d.channelId,
      handle: d.handle,
      subscriberCount: d.subscriberCount,
      syncedAt: new Date().toISOString(),
    });
    setYtDetails(d);
    if (mode === 'link' && !displayName.trim() && d.title) setDisplayName(d.title);
    if (d.subsDisplay) setSubsDisplay(d.subsDisplay);
    // On refresh, custom (non-YouTube-hosted) image URLs are preserved.
    if (d.avatarUrl && (mode === 'link' || !avatarUrl.trim() || isYouTubeHosted(avatarUrl))) {
      setAvatarUrl(d.avatarUrl);
    }
    if (d.bannerUrl && (mode === 'link' || !bannerUrl.trim() || isYouTubeHosted(bannerUrl))) {
      setBannerUrl(d.bannerUrl);
    }
  };

  const linkChannel = async (channelId: string) => {
    setYtBusy(true);
    try {
      const details = await lookupTakeoverChannel(channelId);
      applyDetails(details, 'link');
      setYtQuery('');
      setYtResults([]);
      setYtNote(null);
    } catch (err: any) {
      notify('error', 'Could not fetch channel from YouTube', err?.message);
    } finally {
      setYtBusy(false);
    }
  };

  const refreshFromYouTube = async () => {
    if (!youtube) return;
    setYtBusy(true);
    try {
      const details = await lookupTakeoverChannel(youtube.channelId);
      applyDetails(details, 'refresh');
      notify('success', 'Refreshed from YouTube', details.subsDisplay);
    } catch (err: any) {
      notify('error', 'Refresh failed', err?.message);
    } finally {
      setYtBusy(false);
    }
  };

  const unlinkChannel = () => {
    setYoutube(null);
    setYtDetails(null);
  };

  const save = async () => {
    if (!displayName.trim()) {
      notify('error', 'Display name is required');
      return;
    }
    const badContact = contacts.find((c) => !c.name.trim() || !/.+@.+\..+/.test(c.email));
    if (badContact) {
      notify('error', 'Contacts need a name and a valid email');
      return;
    }
    if (tiers.some((t) => !t.label.trim())) {
      notify('error', 'Budget tiers need labels');
      return;
    }
    setSaving(true);
    const input: CreatorInput = {
      displayName: displayName.trim(),
      blurb: blurb.trim() || undefined,
      subsDisplay: subsDisplay.trim() || undefined,
      avatarUrl: avatarUrl.trim() || undefined,
      bannerUrl: bannerUrl.trim() || undefined,
      youtube, // null clears the link on PATCH; ignored on create
      active,
      contacts,
      budgetTiers: tiers.map((t, i) => ({ ...t, sortOrder: i })),
    };
    try {
      const saved = creator
        ? await updateTakeoverCreator(creator.id, input)
        : await createTakeoverCreator(input);
      onSaved(saved);
      notify('success', creator ? 'Creator updated' : 'Creator created', saved.publicUrl);
      onClose();
    } catch (err: any) {
      notify('error', 'Save failed', err?.message);
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!creator) return;
    if (!window.confirm('Regenerate the request link? The current link stops working immediately.')) return;
    setRegenerating(true);
    try {
      const updated = await regenerateTakeoverSlug(creator.id);
      onSaved(updated);
      notify('success', 'Link regenerated', updated.publicUrl);
      onClose();
    } catch (err: any) {
      notify('error', 'Regenerate failed', err?.message);
    } finally {
      setRegenerating(false);
    }
  };

  const copyLink = async () => {
    if (!creator) return;
    await navigator.clipboard.writeText(creator.publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const chipAvatar = ytDetails?.avatarUrl || avatarUrl;

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Takeovers"
      title={creator ? `Edit ${creator.displayName}` : 'Add creator'}
      width={640}
      closeOnEsc={false}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : creator ? 'Save changes' : 'Create creator'}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {creator && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--bg-surface-2)',
              borderRadius: 'var(--radius-md)',
              padding: '10px 12px',
            }}
          >
            <code style={{ fontSize: 12.5, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {creator.publicUrl}
            </code>
            <Button size="sm" variant="subtle" onClick={copyLink}>{copied ? 'Copied ✓' : 'Copy link'}</Button>
            <Button size="sm" variant="ghost" onClick={regenerate} disabled={regenerating}>
              {regenerating ? 'Regenerating…' : 'Regenerate'}
            </Button>
          </div>
        )}

        {/* YouTube channel link — the auto-fill source */}
        <div>
          <label style={labelStyle}>YouTube channel</label>
          {youtube ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid var(--border-1)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 12px',
                background: 'var(--bg-surface-2)',
              }}
            >
              {chipAvatar && (
                <img
                  key={chipAvatar}
                  src={chipAvatar}
                  alt=""
                  onError={hideOnError}
                  style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ytDetails?.title || displayName || youtube.channelId}
                  {youtube.handle && (
                    <span style={{ color: 'var(--fg-4)', fontWeight: 500, marginLeft: 6 }}>{youtube.handle}</span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>
                  {subsDisplay || '—'} · synced {fmtSynced(youtube.syncedAt)}
                </div>
              </div>
              <Button size="sm" variant="subtle" onClick={refreshFromYouTube} disabled={ytBusy}>
                {ytBusy ? 'Refreshing…' : '↻ Refresh'}
              </Button>
              <Button size="sm" variant="ghost" onClick={unlinkChannel} disabled={ytBusy}>Unlink</Button>
            </div>
          ) : (
            <div style={{ position: 'relative' }}>
              <input
                style={fieldStyle}
                value={ytQuery}
                onChange={(e) => setYtQuery(e.target.value)}
                placeholder="Search your network or YouTube — or paste a channel URL / @handle"
                disabled={ytBusy}
              />
              {ytQuery.trim().length >= 2 && (ytSearching || ytResults.length > 0 || ytNote) && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    zIndex: 5,
                    background: 'var(--bg-surface-1)',
                    border: '1px solid var(--border-1)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
                    maxHeight: 280,
                    overflowY: 'auto',
                  }}
                >
                  {ytSearching && (
                    <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--fg-4)' }}>Searching…</div>
                  )}
                  {!ytSearching && ytResults.length === 0 && (
                    <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--fg-4)' }}>No channels found.</div>
                  )}
                  {!ytSearching &&
                    ytResults.map((c) => (
                      <button
                        key={c.channelId}
                        type="button"
                        onClick={() => linkChannel(c.channelId)}
                        disabled={ytBusy}
                        style={{
                          display: 'flex',
                          width: '100%',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 12px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textAlign: 'left',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {c.thumbnailUrl ? (
                          <img
                            src={c.thumbnailUrl}
                            alt=""
                            onError={hideOnError}
                            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: 'var(--bg-surface-3, var(--bg-surface-2))',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 13,
                              fontWeight: 700,
                              color: 'var(--fg-4)',
                              flexShrink: 0,
                            }}
                          >
                            {c.title.slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--fg-1)' }}>
                            {c.title}
                          </div>
                          {c.subsDisplay && (
                            <div style={{ fontSize: 11.5, color: 'var(--fg-4)' }}>{c.subsDisplay}</div>
                          )}
                        </div>
                        {c.inNetwork && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              color: 'var(--accent)',
                              border: '1px solid var(--accent)',
                              borderRadius: 999,
                              padding: '2px 7px',
                              flexShrink: 0,
                            }}
                          >
                            Network
                          </span>
                        )}
                      </button>
                    ))}
                  {ytNote && (
                    <div style={{ padding: '8px 12px', fontSize: 11.5, color: 'var(--warning)' }}>{ytNote}</div>
                  )}
                </div>
              )}
              <div style={hintStyle}>
                Linking auto-fills name, subscribers, avatar and banner. Everything stays editable.
              </div>
            </div>
          )}
          {youtube && (
            <div style={hintStyle}>
              Refresh updates subscribers and YouTube-hosted images; custom image URLs are kept.
            </div>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Display name *</label>
            <input style={fieldStyle} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Kai Cenat" />
          </div>
          <div>
            <label style={labelStyle}>Subscribers (display text)</label>
            <input style={fieldStyle} value={subsDisplay} onChange={(e) => setSubsDisplay(e.target.value)} placeholder="2.4M subscribers" />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 2 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Page active (uncheck to take the request page offline)
            </label>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Blurb (shown on the public page)</label>
            <textarea style={{ ...fieldStyle, minHeight: 64, resize: 'vertical' }} value={blurb} onChange={(e) => setBlurb(e.target.value)} placeholder="Short positioning blurb advertisers see on the request page." />
          </div>
          <div>
            <label style={labelStyle}>Avatar URL</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {avatarUrl.trim() && (
                <img
                  key={avatarUrl}
                  src={avatarUrl}
                  alt=""
                  onError={hideOnError}
                  style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border-1)', flexShrink: 0 }}
                />
              )}
              <input style={{ ...fieldStyle, flex: 1 }} value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder="https://…" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Banner URL</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {bannerUrl.trim() && (
                <img
                  key={bannerUrl}
                  src={bannerUrl}
                  alt=""
                  onError={hideOnError}
                  style={{ width: 76, height: 34, borderRadius: 6, objectFit: 'cover', border: '1px solid var(--border-1)', flexShrink: 0 }}
                />
              )}
              <input style={{ ...fieldStyle, flex: 1 }} value={bannerUrl} onChange={(e) => setBannerUrl(e.target.value)} placeholder="https://…" />
            </div>
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Notification contacts</label>
            <Button size="sm" variant="ghost" onClick={() => setContacts([...contacts, { name: '', email: '', notify: true }])}>
              + Add contact
            </Button>
          </div>
          {contacts.length === 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--fg-4)' }}>No contacts — creator notifications will be skipped.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {contacts.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  style={{ ...fieldStyle, flex: 1 }}
                  value={c.name}
                  placeholder="Name"
                  onChange={(e) => setContacts(contacts.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
                <input
                  style={{ ...fieldStyle, flex: 1.4 }}
                  value={c.email}
                  placeholder="email@example.com"
                  onChange={(e) => setContacts(contacts.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                />
                <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={c.notify}
                    onChange={(e) => setContacts(contacts.map((x, j) => (j === i ? { ...x, notify: e.target.checked } : x)))}
                  />
                  notify
                </label>
                <Button size="sm" variant="ghost" onClick={() => setContacts(contacts.filter((_, j) => j !== i))}>✕</Button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>Budget tiers (public dropdown)</label>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setTiers([...tiers, { id: `tier-${Date.now()}`, label: '', sortOrder: tiers.length }])}
            >
              + Add tier
            </Button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tiers.map((t, i) => (
              <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  style={{ ...fieldStyle, flex: 1.4 }}
                  value={t.label}
                  placeholder="$10–25K"
                  onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                />
                <input
                  style={{ ...fieldStyle, width: 110 }}
                  type="number"
                  value={t.minUsd ?? ''}
                  placeholder="min $"
                  onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, minUsd: e.target.value === '' ? undefined : Number(e.target.value) } : x)))}
                />
                <input
                  style={{ ...fieldStyle, width: 110 }}
                  type="number"
                  value={t.maxUsd ?? ''}
                  placeholder="max $"
                  onChange={(e) => setTiers(tiers.map((x, j) => (j === i ? { ...x, maxUsd: e.target.value === '' ? undefined : Number(e.target.value) } : x)))}
                />
                <Button size="sm" variant="ghost" disabled={tiers.length <= 1} onClick={() => setTiers(tiers.filter((_, j) => j !== i))}>✕</Button>
              </div>
            ))}
          </div>
          <div style={hintStyle}>
            Min/max power the pipeline estimate in Analytics; only the label is shown to advertisers.
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreatorEditor;
